/*
 * PlacesInsights.tsx
 * ===========
 * Visualize some statistics related to places.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Carousel, Divider, Tooltip } from "antd";
import { BarChart } from "echarts/charts";
import * as echarts from "echarts/core";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import React, { useEffect, useRef } from "react";
import spacetime from "spacetime";
import styled from "styled-components";
import DateUtils from "ts/shared/DateUtils";
import ProtoUtils from "ts/shared/ProtoUtils";
import "@material-design-icons/font/outlined.css";
// Import the components you need (e.g., Title, Tooltip, Grid, Axes)
import { GridComponent, TitleComponent, TooltipComponent, } from "echarts/components";
// Import the renderer (Canvas or SVG)
import { CanvasRenderer } from "echarts/renderers";
import { stripAddress } from "ts/shared/EventUtils";
// Register the required components and charts
echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    BarChart,
    CanvasRenderer
]);
const ContainerDiv = styled.div `
  position: absolute;
  width: 800px;
  height: 93vh;
  top: 0;
  left: 5%;
  box-sizing: border-box;
  pointer-events: all;
  border-radius: 15px;
  background-color: rgba(0, 0, 0, 0.5);
  /* border: 1px solid red; */
`;
const ScrollDiv = styled.div `
  position: relative;
  width: 100%;
  height: 82vh;
  overflow: auto;
  overflow-y: scroll;
  padding-right: 10px;
  pointer-events: all;
  text-align: left;
  &::-webkit-scrollbar {
    height: 5px;
    width: 5px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(128, 128, 128, 0.5);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  /* border: 1px solid blue; */
`;
const ContentDiv = styled.div `
  padding: 30px;
  /* border: 1px solid red; */
  width: 100%;
  height: 100%;
`;
const ContentHeading = styled.div `
  margin-bottom: 25px;
  font-size: 36px;
  font-weight: bold;
`;
const StyledCarousel = styled(Carousel) `
  height: 100%;
`;
const TopPlaceContainerDiv = styled.div `
  width: 100%;
`;
const TopPlaceDescriptionDiv = styled.div `
  font-size: 18px;
  margin-bottom: 25px;
`;
const TopPlaceDiv = styled.div `
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  padding-top: 10px;
  padding-bottom: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  /* border-bottom: 1px solid rgba(255, 255, 255, 0.5); */
  &:hover {
    background-color: rgba(128, 128, 128, 0.5);
  }
  &.selected {
    background-color: rgba(128, 128, 128, 0.5);
  }
`;
const TopPlaceNVisitsDiv = styled.div `
  width: 125px;
  height: 100%;
  font-size: 24px;
  text-align: center;
  justify-content: center;
  align-items: center;
  /* border: 1px solid red; */
`;
const TopPlaceInfoDiv = styled.div `
  flex-grow: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  /* border: 1px solid red; */
`;
const TopPlaceIcon = styled.span `
  margin-right: 15px;
  font-size: 20px;
  color: white;
`;
const TopPlaceTitleDiv = styled.div `
  height: 100%;
  font-size: 18px;
  /* border: 1px solid blue; */
`;
const VisitPeriodDiv = styled.div `
  color: rgb(255, 122, 117);
  font-size: 36px;
  text-align: center;
`;
const VisitPeriodDescriptionDiv = styled.div `
  color: white;
  font-size: 18px;
  text-align: center;
  margin-top: -10px;
`;
const VisitRowDiv = styled.div `
  display: flex;
  align-items: flex-start;
  margin-bottom: 5px;
`;
const VisitYearDiv = styled.div `
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 18px;
`;
const VisitLabelDiv = styled.div `
  width: 75px;
  font-size: 20px;
  font-variant: all-small-caps;
  text-align: center;
`;
const VisitDiv = styled.div `
  width: 15px;
  height: 30px;
  margin-right: 5px;
  border-radius: 10px;
  border: 1px solid white;
  background: rgba(255, 255, 255, 0.5);
  &.isFuture {
    border: 1px solid darkgray;
    background: rgba(64, 64, 64, 0.5);
  }
  &.hasVisit {
    background: rgb(247, 91, 85);
  }
  &.hasVisit:hover {
    border: 2px solid cyan;
  }
`;
const VisitTooltipDateDiv = styled.div `
  font-size: 12px;
  text-align: center;
`;
const VisitTooltipEventNameDiv = styled.div `
  font-size: 16px;
  text-align: center;
`;
const VisitTooltipPlaceNameDiv = styled.div `
  font-size: 18px;
  text-align: center;
`;
const ChartDiv = styled.div `
  margin-left: 10%;
  width: 80%;
  height: 250px;
  justify-content: center;
  /* padding: 25px; */
`;
const ListFooterDiv = styled.div `
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;
const RecContainerDiv = styled.div `
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  padding: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  /* border-bottom: 1px solid rgba(255, 255, 255, 0.2); */
  &:hover {
    background-color: rgba(128, 128, 128, 0.5);
  }
  &.selected {
    background-color: rgba(128, 128, 128, 0.5);
  }
`;
const RecInfoDiv = styled.div `
  flex-grow: 1;
  padding-left: 20px;
`;
const RecTitleDiv = styled.div `
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
`;
const RecSimilarToDiv = styled.div `
  font-style: italic;
`;
const RecReasonDiv = styled.div `
  margin-top: 10px;
`;
const ThumbnailBasePath = "assets/thumbnails/foodanddrink";
const recs = [
    {
        name: "Foreign Cinema",
        thumbnail: `${ThumbnailBasePath}/foreigncinema.png`,
    },
    {
        name: "State Bird Provisions",
        thumbnail: `${ThumbnailBasePath}/statebirdsf.png`,
    },
    {
        name: "Nopa",
        thumbnail: `${ThumbnailBasePath}/nopasf.png`,
    },
    {
        name: "R&G Lounge",
        thumbnail: `${ThumbnailBasePath}/rnglounge.png`,
    },
    {
        name: "Roka Akor",
        thumbnail: `${ThumbnailBasePath}/rokaakorsf.png`,
    },
    {
        name: "Zuni Café",
        thumbnail: `${ThumbnailBasePath}/zunicafe.png`,
    },
    {
        name: "Burma Superstar",
        thumbnail: `${ThumbnailBasePath}/burmasuperstar.png`,
    },
    {
        name: "SPQR",
        thumbnail: `${ThumbnailBasePath}/spqr.png`,
    },
    {
        name: "Gracias Madre",
        thumbnail: `${ThumbnailBasePath}/graciasmadre.png`,
    },
    {
        name: "Liholiho Yacht Club",
        thumbnail: `${ThumbnailBasePath}/liholihoyachtclub.png`,
    },
    {
        name: "Rich Table",
        thumbnail: `${ThumbnailBasePath}/richtable.png`,
    },
    {
        name: "Souvla",
        thumbnail: `${ThumbnailBasePath}/souvla.png`,
    },
    {
        name: "Marufuku Ramen",
        thumbnail: `${ThumbnailBasePath}/marufukuramen.png`,
    },
    {
        name: "Bar Agricole",
        thumbnail: `${ThumbnailBasePath}/baragricole.png`,
    },
    {
        name: "A16",
        thumbnail: `${ThumbnailBasePath}/a16.png`,
    }
];
const DaysOfWeekChartOptions = createChartOption();
DaysOfWeekChartOptions.xAxis["data"] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
DaysOfWeekChartOptions.series[0].data = [14, 21, 18, 17, 35, 43, 25];
const TimesOfDayChartOptions = createChartOption();
TimesOfDayChartOptions.xAxis["data"] = [
    '12am', '1am', '2am', '3am', '4am', '5am', '6am', '7am', '8am', '9am', '10am', '11am',
    '12pm', '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm', '8pm', '9pm', '10pm', '11pm'
];
TimesOfDayChartOptions.xAxis["axisLabel"].interval = 2;
TimesOfDayChartOptions.series[0].data = [
    10, 8, 7, 6, 8, 15, 35, 62, 88, 95, 80, 75,
    70, 68, 82, 90, 85, 78, 65, 55, 45, 30, 25, 15
];
let topPlaces = [];
let visitsByDate = {};
let visitYears = [2025, 2024];
class PlacesInsightsProps {
    userMap;
    activityInsights;
}
class PlacesInsightsState {
    topPlaceSelected;
    recSelected;
    activeSlide;
}
let PlacesInsights = class PlacesInsights extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            topPlaceSelected: "",
            recSelected: "",
            activeSlide: 0
        };
    }
    getTopPlaces() {
        if (topPlaces.length > 0)
            return topPlaces;
        const foodAndDrink = this.props.activityInsights.foodAndDrink;
        if (!foodAndDrink)
            return [];
        const places = foodAndDrink.visitedFoodAndDrinkPlaces;
        const tmpTopPlaces = [];
        for (const place of places) {
            const placeStats = foodAndDrink.placeStatistics[place.placeUid];
            if (!placeStats) {
                console.warn(`No place stats for ${place.displayNames[0].name})`);
            }
            const strippedName = stripAddress(place.displayNames[0].name);
            if (!strippedName) {
                // console.log("Place is an address: ");
                // console.log(place.displayNames.map(v => v.name));
                continue;
            }
            if (place.displayNames[0].name !== strippedName) {
                // console.log("Converting name from " + place.displayNames[0].name + " to " + strippedName);
                place.displayNames[0].name = strippedName;
            }
            const topPlace = {
                place: place,
                events: placeStats.allEvents
            };
            tmpTopPlaces.push(topPlace);
        }
        tmpTopPlaces.sort((a, b) => {
            return b.events.length - a.events.length;
        });
        topPlaces = tmpTopPlaces;
        let placeCount = 0;
        for (const event of foodAndDrink.eatingActivityStatistics.allEvents) {
            let name = event.eventName;
            let placeName = "";
            let place;
            if (event.placeUid) {
                place = places.find(p => p.placeUid === event.placeUid);
                if (place) {
                    name = place.displayNames[0].name;
                    placeName = place.displayNames[0].name;
                }
                placeCount++;
            }
            const visit = {
                st: ProtoUtils.toSpacetime(event.startDate, DateUtils.defaultTimezone),
                eventName: event.eventName,
                placeName: placeName,
                event: toJS(event),
                place: place
            };
            if (!visitYears.includes(visit.st.year())) {
                visitYears.push(visit.st.year());
            }
            visitsByDate[visit.st.startOf("day").toNativeDate().getTime()] = visit;
        }
        visitYears.sort((a, b) => b - a);
        // console.log(placeCount + "/" + foodAndDrink.eatingActivityStatistics.allEvents.length);
        this.showAllTopPlacesOnMap();
        return topPlaces;
    }
    getTopPlaceIconName(type) {
        type = type.toLowerCase();
        if (type === "bar" || type === "cafe") {
            return `local_${type}`;
        }
        return type;
    }
    getVisitPeriodString() {
        const foodAndDrink = this.props.activityInsights.foodAndDrink;
        if (!foodAndDrink) {
            return "2.5 weeks";
        }
        const seconds = foodAndDrink.eatingActivityStatistics.activityIntervalDistribution.medianIntervalSeconds;
        return DateUtils.getPeriodString(seconds);
    }
    showAllTopPlacesOnMap() {
        const userMap = this.props.userMap;
        const topPlaces = this.getTopPlaces();
        if (!(topPlaces.length > 0))
            return;
        const places = topPlaces.map(topPlace => topPlace.place);
        userMap.updatePlaceLayerSourceData(places);
        userMap.showMarkerSet(places, false);
    }
    showTopPlaceOnMap(place) {
        const userMap = this.props.userMap;
        const center = place.geolocationRegion.center;
        const coords = [center.longitude, center.latitude];
        userMap.flyTo(coords);
    }
    showVisitOnMap(visit) {
        const userMap = this.props.userMap;
        const center = visit.place.geolocationRegion.center;
        const coords = [center.longitude, center.latitude];
        userMap.flyTo(coords);
    }
    showAllRecommendationsOnMap() {
        const userMap = this.props.userMap;
        const activityInsights = this.props.activityInsights;
        const recommendations = activityInsights.foodAndDrink?.restaurantRecommendations?.recommendations;
        if (!recommendations)
            return;
        const places = recommendations.map(rec => rec.place);
        userMap.clearPlaceLayerSourceData();
        userMap.showMarkerSet(places, true);
    }
    showRecommendationOnMap(rec) {
        const userMap = this.props.userMap;
        const center = rec.place.geolocationRegion.center;
        const coords = [center.longitude, center.latitude];
        userMap.flyTo(coords);
    }
    getRecThumbnail(recName) {
        const rec = recs.find(rec => rec.name === recName);
        return rec?.thumbnail;
    }
    getDaysOfWeekData() {
        const eatingStats = this.props.activityInsights?.foodAndDrink?.eatingActivityStatistics;
        const dayOfWeekCounts = eatingStats?.activityStartTimeDistribution?.dayOfWeekCounts;
        if (dayOfWeekCounts) {
            const data = dayOfWeekCounts.map(v => v.totalActivitySessionsOnDay);
            return data;
        }
        return [];
    }
    getTimesOfDayData() {
        const eatingStats = this.props.activityInsights?.foodAndDrink?.eatingActivityStatistics;
        const dayOfWeekCounts = eatingStats?.activityStartTimeDistribution?.dayOfWeekCounts;
        if (!dayOfWeekCounts) {
            return [];
        }
        const data = new Array(24).fill(0);
        for (let dayData of dayOfWeekCounts) {
            for (let i = 0; i < 24; ++i) {
                const oneHourSpan = dayData.startTimeBinCounts.slice(i * 4, i * 4 + 4);
                data[i] += oneHourSpan.reduce((acc, currVal) => acc + currVal, 0);
            }
        }
        return data;
    }
    onChangeSlide = (currentSlide, nextSlide) => {
        if (nextSlide === 0) {
            this.showAllTopPlacesOnMap();
        }
        else if (nextSlide === 1) {
        }
        else if (nextSlide === 2) {
            this.showAllRecommendationsOnMap();
        }
        this.setState({ activeSlide: nextSlide });
    };
    render() {
        const activityInsights = this.props.activityInsights;
        return (React.createElement(ContainerDiv, null,
            React.createElement(StyledCarousel, { arrows: true, dotPosition: "top", infinite: false, beforeChange: this.onChangeSlide },
                React.createElement(ContentDiv, null,
                    React.createElement(ContentHeading, null, "Top Food & Drink"),
                    React.createElement(TopPlaceDescriptionDiv, null, "Your top destinations, ranked by number of visits."),
                    React.createElement(ScrollDiv, null,
                        this.getTopPlaces().map((topPlace, i) => {
                            const place = topPlace.place;
                            const placeId = place.placeUid;
                            return (React.createElement(TopPlaceContainerDiv, { key: placeId },
                                React.createElement(TopPlaceDiv, { className: this.state.topPlaceSelected === placeId && "selected", onClick: () => {
                                        if (placeId === this.state.topPlaceSelected) {
                                            this.setState({ topPlaceSelected: "" });
                                            this.showAllTopPlacesOnMap();
                                        }
                                        else {
                                            this.setState({ topPlaceSelected: placeId });
                                            this.showTopPlaceOnMap(place);
                                        }
                                    } },
                                    React.createElement(TopPlaceNVisitsDiv, null, topPlace.events.length),
                                    React.createElement(TopPlaceInfoDiv, null,
                                        React.createElement(TopPlaceIcon, { className: "material-icons-outlined" }, this.getTopPlaceIconName(place.placeCategory)),
                                        React.createElement(TopPlaceTitleDiv, null, place.displayNames[0].name)))));
                        }),
                        React.createElement(ListFooterDiv, null))),
                React.createElement(ContentDiv, null,
                    React.createElement(ContentHeading, null, "Food & Drink Visits"),
                    React.createElement(ScrollDiv, null,
                        React.createElement(Divider, null),
                        React.createElement(VisitPeriodDescriptionDiv, null, "days of week"),
                        React.createElement(EChartsChart, { chartOptions: DaysOfWeekChartOptions, data: this.getDaysOfWeekData() }),
                        React.createElement(Divider, null),
                        React.createElement(VisitPeriodDescriptionDiv, null, "times of day"),
                        React.createElement(EChartsChart, { chartOptions: TimesOfDayChartOptions, data: this.getTimesOfDayData() }),
                        React.createElement(Divider, null),
                        React.createElement(VisitPeriodDiv, null, this.getVisitPeriodString()),
                        React.createElement(VisitPeriodDescriptionDiv, null, "average time between visits"),
                        this.state.activeSlide === 1 &&
                            visitYears.map(year => React.createElement("div", { key: `visits-${year}` },
                                React.createElement(VisitYearDiv, null, year),
                                Array.from({ length: 12 }, (v, i) => i).map(month => {
                                    const st = spacetime([year, month, 1]);
                                    const today = spacetime.now();
                                    return (React.createElement(VisitRowDiv, { key: `month-${month}` },
                                        React.createElement(VisitLabelDiv, null, st.format('{month-short}')),
                                        Array.from({ length: st.daysInMonth() }, (v, i) => i + 1).map(day => {
                                            const stDate = spacetime([year, month, day]);
                                            const visit = visitsByDate[stDate.toNativeDate().getTime()];
                                            if (!!visit) {
                                                return (React.createElement(Tooltip, { key: `visit-${month}-${day}`, placement: "top", onOpenChange: (open) => {
                                                        this.props.userMap.setHighlighted(visit.place?.placeUid, open);
                                                    }, title: React.createElement(React.Fragment, null,
                                                        React.createElement(VisitTooltipDateDiv, null, visit.st.format("{day-short} {month-short} {date}, {year} at {time}")),
                                                        React.createElement(VisitTooltipPlaceNameDiv, null, visit.placeName)) },
                                                    React.createElement(VisitDiv, { className: "hasVisit", onClick: () => {
                                                            if (process.env.NODE_ENV === "development") {
                                                                console.log(visit.event);
                                                            }
                                                            this.showVisitOnMap(visit);
                                                        } })));
                                            }
                                            else {
                                                return React.createElement(VisitDiv, { key: `visit-${month}-${day}`, className: stDate.isAfter(today) && "isFuture" });
                                            }
                                        })));
                                }))))),
                React.createElement(ContentDiv, null,
                    React.createElement(ContentHeading, null, "Food & Drink Recommendations"),
                    React.createElement(ScrollDiv, null,
                        activityInsights.foodAndDrink &&
                            activityInsights.foodAndDrink?.restaurantRecommendations?.recommendations?.map(rec => React.createElement(RecContainerDiv, { key: rec.name, className: this.state.recSelected === rec.name && "selected", onClick: () => {
                                    if (rec.name === this.state.recSelected) {
                                        this.setState({ recSelected: "" });
                                        this.showAllRecommendationsOnMap();
                                    }
                                    else {
                                        this.setState({ recSelected: rec.name });
                                        this.showRecommendationOnMap(rec);
                                    }
                                } },
                                React.createElement("img", { src: this.getRecThumbnail(rec.name), width: "320px", height: "180px" }),
                                React.createElement(RecInfoDiv, null,
                                    React.createElement(RecTitleDiv, null, rec.name),
                                    React.createElement("div", null, rec.cuisine),
                                    React.createElement(RecSimilarToDiv, null,
                                        "Similar to: ",
                                        rec.similarTo),
                                    React.createElement(RecReasonDiv, null, rec.reason)))),
                        React.createElement(ListFooterDiv, null))))));
    }
};
PlacesInsights = __decorate([
    observer
], PlacesInsights);
export default PlacesInsights;
const EChartsChart = ({ chartOptions, data }) => {
    // Create a ref to attach to the div element
    const chartRef = useRef(null);
    useEffect(() => {
        let chartInstance;
        if (chartRef.current) {
            chartInstance = echarts.init(chartRef.current);
            chartOptions.series[0].data = data;
            chartInstance.setOption(chartOptions);
        }
        return () => {
            if (chartInstance) {
                chartInstance.dispose();
            }
        };
    }, [data, chartOptions]);
    return (React.createElement(ChartDiv, { ref: chartRef }));
};
function createChartOption() {
    return {
        grid: { top: 8, right: 8, bottom: 24, left: 36 },
        xAxis: {
            type: "category",
            data: [],
            axisLabel: {
                color: "white"
            }
        },
        yAxis: {
            type: "value",
            axisLabel: {
                color: "gray"
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: "gray",
                    type: "dotted"
                }
            }
        },
        series: [{
                data: [],
                type: "bar",
                barMaxWidth: "45px",
                itemStyle: {
                    color: "rgba(0, 255, 255, 0.8)"
                },
            }],
    };
}
